import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, XCircle, Clock, AlertTriangle, Bell, User } from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';

interface CommitmentViewProps {
  companyId: string;
}

interface Commitment {
  id: string;
  worker_id: string;
  worker_name: string;
  committed_action: string;
  time_commitment: string;
  expected_completion: string;
  actual_completion: string | null;
  was_fulfilled: boolean | null;
  follow_up_sent: boolean;
  supervisor_notified: boolean;
  created_at: string;
}

export function CommitmentView({ companyId }: CommitmentViewProps) {
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'fulfilled' | 'overdue'>('all');
  const [stats, setStats] = useState({
    total: 0,
    fulfilled: 0,
    pending: 0,
    overdue: 0,
    fulfillmentRate: 0
  });

  useEffect(() => {
    loadCommitments();
  }, [companyId]);

  const loadCommitments = async () => {
    try {
      // First get worker profiles for this company
      const { data: workers, error: workersError } = await supabase
        .from('worker_profiles')
        .select('id, display_name')
        .eq('company_id', companyId);

      if (workersError) throw workersError;

      const workerIds = (workers || []).map(w => w.id);
      const workerMap = new Map((workers || []).map(w => [w.id, w.display_name]));

      if (workerIds.length === 0) {
        setCommitments([]);
        setLoading(false);
        return;
      }

      const { data: followups, error } = await supabase
        .from('commitment_followups')
        .select('*')
        .in('worker_id', workerIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const commitmentList: Commitment[] = (followups || []).map((f: any) => ({
        id: f.id,
        worker_id: f.worker_id,
        worker_name: workerMap.get(f.worker_id) || 'Unknown Worker',
        committed_action: f.committed_action || 'No action specified',
        time_commitment: f.time_commitment || 'Not specified',
        expected_completion: f.expected_completion,
        actual_completion: f.actual_completion,
        was_fulfilled: f.was_fulfilled,
        follow_up_sent: f.follow_up_sent || false,
        supervisor_notified: f.supervisor_notified || false,
        created_at: f.created_at
      }));

      setCommitments(commitmentList);

      // Calculate stats
      const fulfilled = commitmentList.filter(c => c.was_fulfilled === true).length;
      const overdue = commitmentList.filter(c => 
        c.was_fulfilled !== true && 
        c.expected_completion && 
        isPast(new Date(c.expected_completion))
      ).length;
      const pending = commitmentList.filter(c => 
        c.was_fulfilled === null && 
        (!c.expected_completion || !isPast(new Date(c.expected_completion)))
      ).length;

      setStats({
        total: commitmentList.length,
        fulfilled,
        pending,
        overdue,
        fulfillmentRate: commitmentList.length > 0 ? (fulfilled / commitmentList.length) * 100 : 0
      });
    } catch (error) {
      console.error('Error loading commitments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (commitment: Commitment) => {
    if (commitment.was_fulfilled === true) {
      return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Fulfilled</Badge>;
    }
    if (commitment.was_fulfilled === false) {
      return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Not Fulfilled</Badge>;
    }
    if (commitment.expected_completion && isPast(new Date(commitment.expected_completion))) {
      return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Overdue</Badge>;
    }
    if (commitment.expected_completion && isToday(new Date(commitment.expected_completion))) {
      return <Badge className="bg-orange-500"><Clock className="h-3 w-3 mr-1" />Due Today</Badge>;
    }
    return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
  };

  const filteredCommitments = commitments.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'fulfilled') return c.was_fulfilled === true;
    if (filter === 'overdue') return c.was_fulfilled !== true && c.expected_completion && isPast(new Date(c.expected_completion));
    if (filter === 'pending') return c.was_fulfilled === null && (!c.expected_completion || !isPast(new Date(c.expected_completion)));
    return true;
  });

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading commitments...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:border-primary" onClick={() => setFilter('all')}>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary" onClick={() => setFilter('fulfilled')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm text-muted-foreground">Fulfilled</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.fulfilled}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary" onClick={() => setFilter('pending')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-500" />
              <span className="text-sm text-muted-foreground">Pending</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary" onClick={() => setFilter('overdue')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm text-muted-foreground">Overdue</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Fulfillment Rate</p>
            <p className="text-2xl font-bold">{stats.fulfillmentRate.toFixed(1)}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Commitments List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">
            {filter === 'all' ? 'All Commitments' : 
             filter === 'fulfilled' ? 'Fulfilled Commitments' :
             filter === 'overdue' ? 'Overdue Commitments' : 'Pending Commitments'}
          </CardTitle>
          {filter !== 'all' && (
            <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
              Show All
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {filteredCommitments.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No commitments found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Committed Action</TableHead>
                  <TableHead>Timeframe</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Follow-up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCommitments.map((commitment) => (
                  <TableRow key={commitment.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {commitment.worker_name}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{commitment.committed_action}</TableCell>
                    <TableCell>{commitment.time_commitment}</TableCell>
                    <TableCell>
                      {commitment.expected_completion 
                        ? format(new Date(commitment.expected_completion), 'MMM dd, yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(commitment)}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        {commitment.follow_up_sent && (
                          <span title="Follow-up sent">
                            <Bell className="h-4 w-4 text-blue-500" />
                          </span>
                        )}
                        {commitment.supervisor_notified && (
                          <span title="Supervisor notified">
                            <User className="h-4 w-4 text-purple-500" />
                          </span>
                        )}
                        {!commitment.follow_up_sent && !commitment.supervisor_notified && (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
